import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Tooltip from "antd/es/tooltip";
import { addDays, isSaturday, isWeekend, startOfToday } from "date-fns";
import { useIntl } from "react-intl";

import FrequencyStatus from "@mapmycustomers/shared/enum/frequency/FrequencyStatus";
import Cadenced from "@mapmycustomers/shared/types/base/Cadenced";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import { isNotEmpty } from "@mapmycustomers/shared/util/assert";

import ButtonLink from "@app/component/ButtonLink";
import CollapseCard from "@app/component/CollapseCard";
import DealLockNote from "@app/component/deal/DealLockNote";
import ActivityActions from "@app/component/frequency/ActivityActions";
import ActivityScheduled from "@app/component/frequency/ActivityScheduled";
import FrequencyBox from "@app/component/frequency/FrequencyBox";
import FrequencySelectorWrapper from "@app/component/frequency/FrequencyPanel/component/FrequencySelectorWithConfig";
import NextActivityState from "@app/component/frequency/NextActivityState";
import FrequencyPreviewConfig from "@app/component/frequency/type/FrequencyPreviewConfig";
import LoggedActivityMap from "@app/component/frequency/type/LoggedActivityMap";
import getActivityDayKey from "@app/component/frequency/util/getActivityDayKey";
import getFrequencySequence from "@app/component/frequency/util/getFrequencySequence";
import DealLockReason from "@app/enum/DealLockReason";
import { getReadOnlyDealChecker } from "@app/store/deal";
import { getFrequencyModalConfig, getLoggedActivities } from "@app/store/frequency";
import { fetchLoggedActivities, updateEntityFrequency } from "@app/store/frequency/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import FrequencyCriteria from "@app/types/frequncy/FrequencyCriteria";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { formatDate, formatFriendlyRelativeTime } from "@app/util/formatters";

import EntityTypes from "./EntityTypes";
import styles from "./NextActivities.module.scss";

const ACTIVITIES_PORTION_SIZE = 3;

interface Props {
  config: FrequencyPreviewConfig;
  isReadOnlyDeal: ReturnType<typeof getReadOnlyDealChecker>;
  loggedActivities: LoggedActivityMap;
  onFetchLoggedActivities: typeof fetchLoggedActivities.request;
  updateEntityFrequency: typeof updateEntityFrequency.request;
}

const NextActivities = ({
  config,
  isReadOnlyDeal,
  loggedActivities,
  onFetchLoggedActivities,
  updateEntityFrequency,
}: Props) => {
  const intl = useIntl();
  const [count, setCount] = useState<number>(ACTIVITIES_PORTION_SIZE - 1);
  const analytics = useExtendedAnalytics("To Do", useAnalytics());

  const {
    entity,
    groupsWithFrequencyConfig,
    isEntityFrequencyConfigured,
    isGroupFrequencyConfigured,
  } = config;

  const suggestions: FrequencyCriteria[] = useMemo(() => {
    const frequencies = getFrequencySequence(entity, groupsWithFrequencyConfig)
      .map((frequency) =>
        Array.from({ length: count }, (_, i) => ({
          ...frequency,
          cadenceInterval: frequency.cadenceInterval * (i + 1),
        }))
      )
      .flat()
      .sort((frequency1, frequency2) => frequency1.cadenceInterval - frequency2.cadenceInterval);
    const daysMap = new Map<number, "all" | ActivityType["id"][]>();

    frequencies.forEach((frequency) => {
      const dayRecord = daysMap.get(frequency.cadenceInterval);
      if (dayRecord !== "all") {
        if (isNotEmpty(frequency.crmActivityTypeId)) {
          daysMap.set(
            frequency.cadenceInterval,
            Array.from(new Set([...(dayRecord ?? []), ...frequency.crmActivityTypeId]))
          );
        } else {
          daysMap.set(frequency.cadenceInterval, "all");
        }
      }
    });

    return Array.from(daysMap)
      .slice(0, count)
      .map(([daysCount, types]) => {
        let dueDate = addDays(startOfToday(), daysCount);
        let days = daysCount;
        let dueDateReal;
        if (isWeekend(dueDate)) {
          dueDateReal = dueDate;
          const isDateSaturday = isSaturday(dueDate);
          dueDate = addDays(dueDate, isDateSaturday ? -1 : -2);
          days += isDateSaturday ? -1 : -2;
        }
        return {
          activityTypesIds: types === "all" ? undefined : types,
          days,
          dueDate,
          dueDateReal,
        };
      });
  }, [count, entity, groupsWithFrequencyConfig]);

  const getDates = useCallback(
    (count: number) =>
      (ACTIVITIES_PORTION_SIZE < count
        ? suggestions.slice(count - ACTIVITIES_PORTION_SIZE, count)
        : suggestions
      ).map(({ dueDate }) => dueDate),
    [suggestions]
  );

  useEffect(() => {
    const dates = getDates(count);
    if (dates.length) {
      onFetchLoggedActivities({ dates });
    }
  }, [count, getDates, onFetchLoggedActivities]);

  const handleShowMore = useCallback(() => {
    setCount((count) => count + ACTIVITIES_PORTION_SIZE);
    analytics.clicked(["Show More Suggested Activities"]);
  }, [analytics, setCount]);

  const isSomeActivityScheduled = useCallback(
    (date: Date, types: ActivityType["id"][] | undefined) => {
      const record = loggedActivities[getActivityDayKey(date.toISOString())];
      return !!record && (types?.length ? types.some((type) => record.has(type)) : true);
    },
    [loggedActivities]
  );

  const handleUpdateFrequency = useCallback(
    (cadence: Partial<Cadenced>) => {
      if (entity) {
        updateEntityFrequency({
          entity: {
            ...entity,
            ...cadence,
          },
        });
      }
    },
    [entity, updateEntityFrequency]
  );

  const handleOpenPanel = useCallback(() => {
    analytics.clicked([]);
  }, [analytics]);

  return (
    <div className={styles.container}>
      <AnalyticsContext.Provider value={analytics}>
        {!isEntityFrequencyConfigured && !isGroupFrequencyConfigured ? (
          <FrequencyBox className={styles.noFrequencyContainer}>
            <div className={styles.noFrequency}>
              {intl.formatMessage({
                id: "frequency.frequencyModal.activities.next.noFrequency",
                defaultMessage: "Set a Frequency",
                description:
                  "frequency component - frequency modal - next activities - Set a Frequency",
              })}
            </div>
            {isReadOnlyDeal(entity) ? (
              <DealLockNote reason={DealLockReason.STAGE_READ_ONLY} />
            ) : (
              <FrequencySelectorWrapper onChange={handleUpdateFrequency} />
            )}
          </FrequencyBox>
        ) : (
          <CollapseCard
            header={intl.formatMessage({
              id: "frequency.frequencyModal.activities.next.todo",
              defaultMessage: "to do",
              description: "frequency component - frequency modal - next activities - to do",
            })}
            isOpenByDefault
            onOpen={handleOpenPanel}
          >
            <FrequencyBox className={styles.frequencyBox}>
              <NextActivityState config={config} />
              <div className={styles.frequencyBoxTypes}>
                <EntityTypes activityTypeIds={config.actualFrequencyCriteria?.activityTypesIds} />
              </div>
            </FrequencyBox>
            <div className={styles.nextUp}>
              {intl.formatMessage({
                id: "frequency.frequencyModal.activities.next.textUp",
                defaultMessage: "next up",
                description: "frequency component - frequency modal - next activities - next up ",
              })}
            </div>
            <div className={styles.suggestions}>
              {suggestions.map((criteria) => (
                <div className={styles.suggestion} key={`suggestion-${criteria.dueDate.valueOf()}`}>
                  <div className={styles.suggestionTitle}>
                    <Row align="middle" gutter={layout.spacerS}>
                      <Col>
                        <span>
                          {intl.formatMessage(
                            {
                              id: "frequency.frequencyModal.activities.next.suggestion.title",
                              defaultMessage:
                                "Due in {count} {count, plural, one {Day} other {Days}}",
                              description:
                                "frequency component - frequency modal - next activities - suggestion title",
                            },
                            { count: criteria.days }
                          )}
                        </span>
                      </Col>
                      <Col>
                        <span className={styles.suggestionDate}>
                          <span>{formatDate(criteria.dueDate, "PPPP")}</span>
                          {criteria.dueDateReal && (
                            <Tooltip
                              title={intl.formatMessage(
                                {
                                  id: "frequency.frequencyModal.activities.next.suggestion.changedTooltip",
                                  defaultMessage:
                                    "The original deadline for this activity was set for {date}. However, we have adjusted the deadline to a weekday to ensure the record remains current.",
                                  description:
                                    "frequency component - frequency modal - next activities - suggestion changed tooltip",
                                },
                                {
                                  date: formatFriendlyRelativeTime(
                                    intl,
                                    criteria.dueDateReal.toISOString()
                                  ),
                                }
                              )}
                            >
                              <FontAwesomeIcon className={styles.infoIcon} icon={faInfoCircle} />
                            </Tooltip>
                          )}
                        </span>
                      </Col>
                    </Row>
                    <div>
                      {isSomeActivityScheduled(criteria.dueDate, criteria.activityTypesIds) ? (
                        <ActivityScheduled />
                      ) : (
                        entity.cadenceStatus === FrequencyStatus.UP_TO_DATE && (
                          <ActivityActions criteria={criteria} entity={entity} />
                        )
                      )}
                    </div>
                  </div>
                  <EntityTypes activityTypeIds={criteria.activityTypesIds} />
                </div>
              ))}
            </div>
            <ButtonLink className={styles.showMore} onClick={handleShowMore}>
              {intl.formatMessage({
                id: "frequency.frequencyModal.activities.next.suggestion.showMore",
                defaultMessage: "Show more suggested activities",
                description:
                  "frequency component - frequency modal - next activities - Show more suggested activities",
              })}
            </ButtonLink>
          </CollapseCard>
        )}
      </AnalyticsContext.Provider>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  config: getFrequencyModalConfig(state),
  isReadOnlyDeal: getReadOnlyDealChecker(state),
  loggedActivities: getLoggedActivities(state),
});

const mapDispatchToProps = {
  onFetchLoggedActivities: fetchLoggedActivities.request,
  updateEntityFrequency: updateEntityFrequency.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(NextActivities);
